import React, { useState, useRef } from 'react';
import { transcribeAudio } from '../api';

function notify(message) {
  // No global toast library in this project — fall back to alert so errors
  // aren't silent. Component-local state would be swallowed because the
  // button re-renders on status change.
  if (typeof window !== 'undefined' && window.alert) {
    window.alert(message);
  }
}

function MicrophoneButton({ onTranscribed, disabled = false, className = '' }) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'recording' | 'transcribing'
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const handleClick = async () => {
    if (status === 'transcribing') return;

    if (status === 'recording') {
      const recorder = recorderRef.current;
      if (!recorder) {
        stopTracks();
        setStatus('idle');
        return;
      }
      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        stopTracks();
        setStatus('transcribing');
        try {
          const resp = await transcribeAudio(blob);
          const text = resp?.data?.text || '';
          if (text) {
            onTranscribed(text);
          } else {
            notify('Transcription returned empty result.');
          }
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Transcription failed.';
          notify(`Transcription failed: ${detail}`);
        } finally {
          recorderRef.current = null;
          setStatus('idle');
        }
      };
      try {
        recorder.stop();
      } catch (err) {
        stopTracks();
        recorderRef.current = null;
        setStatus('idle');
        notify(`Failed to stop recording: ${err.message || err}`);
      }
      return;
    }

    // status === 'idle' — start recording
    if (typeof window === 'undefined' || !window.MediaRecorder || !navigator?.mediaDevices?.getUserMedia) {
      notify('Audio recording is not supported in this browser.');
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        notify('Microphone permission was denied.');
      } else {
        notify(`Could not access microphone: ${err.message || err}`);
      }
      return;
    }

    try {
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = (e) => {
        stopTracks();
        recorderRef.current = null;
        setStatus('idle');
        notify(`Recording error: ${e.error?.message || 'unknown error'}`);
      };
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.start();
      setStatus('recording');
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      notify(`Failed to start recording: ${err.message || err}`);
    }
  };

  const title =
    status === 'recording' ? 'Stop recording' :
    status === 'transcribing' ? 'Transcribing…' :
    'Record audio';

  const classes = `btn btn-sm btn-microphone ${status === 'recording' ? 'recording' : ''} ${status === 'transcribing' ? 'transcribing' : ''} ${className}`.trim().replace(/\s+/g, ' ');

  return (
    <button
      type="button"
      className={classes}
      title={title}
      onClick={handleClick}
      disabled={disabled || status === 'transcribing'}
      aria-label={title}
    >
      🎤
    </button>
  );
}

export default MicrophoneButton;
